import React, { useState, useEffect, useContext }  from 'react';
import { UserContext } from '../context/UserContext';
import { Link, useLocation } from 'react-router-dom';
import { Badge, IconButton, SwipeableDrawer, Box, Typography, Button, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { BugReport, Notifications, Logout, Close, MoreHoriz } from '@mui/icons-material';
import group31 from "../assets/loginPage/group-31.svg";
import communityIcon from "../assets/navigationBar/mycommunity.svg";
import joynetIcon from "../assets/navigationBar/myjoynet.svg";
import friendsIcon from "../assets/navigationBar/friends.svg";
import messagesIcon from "../assets/navigationBar/messages.svg";
import profileIcon from "../assets/navigationBar/profile.svg";
import "./css/NavigationBar.css";

/* This component is used to create a navigation bar for the website. It is used within the Layout component. */

const NavigationBar = ({ 
  onBugReportClick, 
  onNotificationClick, 
  onLogout, 
  pendingRequests = [],
  onAcceptRequest,
  onRejectRequest
}) => {
  const location = useLocation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const { userInfo } = useContext(UserContext);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  useEffect(() => {
    const handleStatusChange = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener("online", handleStatusChange);
    window.addEventListener("offline", handleStatusChange);

    return () => {
      window.removeEventListener("online", handleStatusChange);
      window.removeEventListener("offline", handleStatusChange);
    };
  }, []);
  const isOtherUserProfile = location.pathname.startsWith('/profile/') && location.pathname !== '/profile';
  
  const renderLinks = (isMobile = false) => (
    <>
      <Link to="/" className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}>
        <img src={communityIcon} alt="Home" className="nav-icon" />
        {!isMobile && <span>Home</span>}
      </Link>
      <Link to="/myjoynet" className={`nav-item ${location.pathname === '/myjoynet' ? 'active' : ''}`}>
        <img src={joynetIcon} alt="My JoyNet" className="nav-icon" />
        {!isMobile && <span>My JoyNet</span>}
      </Link>
      <Link to="/friends" className={`nav-item ${location.pathname === '/friends' ? 'active' : ''}`}>
        <img src={friendsIcon} alt="Friends" className="nav-icon" />
        {!isMobile && <span>Friends</span>}
      </Link>
      <Link to="/messages" className={`nav-item ${location.pathname === '/messages' ? 'active' : ''}`}>
        <img src={messagesIcon} alt="Messages" className="nav-icon" />
        {!isMobile && <span>Messages</span>}
      </Link>
      <Link to="/profile" className={`nav-item ${location.pathname === '/profile' ? 'active' : ''}`}>
        {userInfo?.profilePictureUrl ? (
          <div className="profile-picture-wrapper">
            <img src={userInfo?.profilePictureUrl || profileIcon} alt="Profile" className="profile-picture" />
            <span className={`status-dot ${isOnline ? 'online' : 'offline'}`}></span>
          </div>
        ) : (
          <img src={profileIcon} alt="Profile" className="nav-icon" />
        )}
        {!isMobile && <span>Profile</span>}
      </Link>
      {isOtherUserProfile && (
        <div className="nav-item active">
          <img src={profileIcon} alt="Other's Profile" className="nav-icon" />
          {!isMobile && <span>Profile</span>}
        </div>
      )}

      {!isOnline && !isMobile && (
        <div className="offline-message">
          You are offline
        </div>
      )}
    </>
  );


  return (
    <nav className="nav-container">
      <div className="nav-desktop">
        <div className="nav-logo">
          <img src={group31} alt="JoyNet Logo" className="logo" />
          <span className="logo-text">JoyNet</span>
        </div>

        <div className="nav-links">
          {renderLinks()}
        </div>
      </div>
      <div className="nav-mobile">
        {renderLinks(true)}
        
        {/* More button for mobile toolbar actions */}
        <button 
          className="nav-item mobile-more-btn"
          onClick={() => setMoreMenuOpen(true)}
        >
          <Badge 
            variant="dot" 
            invisible={pendingRequests.length === 0}
            sx={{ 
              '& .MuiBadge-dot': { 
                backgroundColor: '#E9505F',
                width: 8,
                height: 8,
              } 
            }}
          >
            <MoreHoriz sx={{ width: 24, height: 24, color: '#707496' }} />
          </Badge>
        </button>
      </div>

      {/* Bottom Sheet Menu for Mobile Actions */}
      <SwipeableDrawer
        anchor="bottom"
        open={moreMenuOpen}
        onClose={() => setMoreMenuOpen(false)}
        onOpen={() => setMoreMenuOpen(true)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '60vh',
            pb: 'calc(env(safe-area-inset-bottom, 0px) + 16px)', // Account for safe area only
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Drag handle */}
          <Box 
            sx={{ 
              width: 40, 
              height: 4, 
              backgroundColor: '#ddd', 
              borderRadius: 2, 
              mx: 'auto', 
              mb: 2 
            }} 
          />
          
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
            Quick Actions
          </Typography>
          
          <List>
            <ListItem 
              button 
              onClick={() => {
                setMoreMenuOpen(false);
                setNotificationsOpen(true);
              }}
              sx={{ borderRadius: 2, mb: 1 }}
            >
              <ListItemIcon>
                <Badge 
                  badgeContent={pendingRequests.length} 
                  color="error"
                  max={9}
                >
                  <Notifications sx={{ color: '#272657' }} />
                </Badge>
              </ListItemIcon>
              <ListItemText 
                primary="Notifications" 
                secondary={pendingRequests.length > 0 ? `${pendingRequests.length} pending request${pendingRequests.length > 1 ? 's' : ''}` : 'No new notifications'}
              />
            </ListItem>
            
            <ListItem 
              button 
              onClick={() => {
                setMoreMenuOpen(false);
                onBugReportClick?.();
              }}
              sx={{ borderRadius: 2, mb: 1 }}
            >
              <ListItemIcon>
                <BugReport sx={{ color: '#272657' }} />
              </ListItemIcon>
              <ListItemText primary="Report a Bug" secondary="Help us improve JoyNet" />
            </ListItem>
            
            <Divider sx={{ my: 2 }} />
            
            <ListItem 
              button 
              onClick={() => {
                setMoreMenuOpen(false);
                onLogout?.();
              }}
              sx={{ 
                borderRadius: 2, 
                backgroundColor: 'rgba(233, 80, 95, 0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(233, 80, 95, 0.2)',
                }
              }}
            >
              <ListItemIcon>
                <Logout sx={{ color: '#E9505F' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Log Out" 
                sx={{ '& .MuiListItemText-primary': { color: '#E9505F', fontWeight: 500 } }}
              />
            </ListItem>
          </List>
        </Box>
      </SwipeableDrawer>

      {/* Notifications Bottom Sheet */}
      <SwipeableDrawer
        anchor="bottom"
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        onOpen={() => setNotificationsOpen(true)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: '70vh',
            pb: 'calc(60px + env(safe-area-inset-bottom, 0px) + 16px)', // Account for bottom nav bar
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Drag handle */}
          <Box 
            sx={{ 
              width: 40, 
              height: 4, 
              backgroundColor: '#ddd', 
              borderRadius: 2, 
              mx: 'auto', 
              mb: 2 
            }} 
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
            <IconButton onClick={() => setNotificationsOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          
          {pendingRequests.length > 0 ? (
            <List>
              {pendingRequests.map((request, index) => (
                <ListItem 
                  key={index}
                  sx={{ 
                    flexDirection: 'column', 
                    alignItems: 'flex-start',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 2,
                    mb: 1,
                    p: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 500, mb: 1 }}>
                    {request.senderName} sent you a friend request
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                    <Button 
                      variant="contained" 
                      size="small"
                      onClick={() => {
                        onAcceptRequest?.(request);
                      }}
                      sx={{ 
                        flex: 1, 
                        backgroundColor: '#9D79C6',
                        '&:hover': { backgroundColor: '#8a6ab3' }
                      }}
                    >
                      Accept
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => {
                        onRejectRequest?.(request);
                      }}
                      sx={{ 
                        flex: 1,
                        borderColor: '#ddd',
                        color: '#666',
                        '&:hover': { borderColor: '#bbb' }
                      }}
                    >
                      Decline
                    </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Notifications sx={{ fontSize: 48, color: '#ddd', mb: 2 }} />
              <Typography color="text.secondary">
                No new notifications
              </Typography>
            </Box>
          )}
        </Box>
      </SwipeableDrawer>
    </nav>
  );
};

export default NavigationBar;